import * as turf from "@turf/turf";
import L from "leaflet";
import type React from "react";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import { generateUID } from "verdi-common/utils/HelperFunctions";
import { EventHandler } from "verdi-web-client/HelperFunctions";
import type { Zone } from "verdi-web-client/Models";
import { BREAKPOINT } from "@/configs/breakpoints";
import { shouldBlockMapEntityCardOpen } from "@/features/add-device/should-block-map-entity-card-open";
import { openZustandCard } from "@/features/cards/shared/legacy/open-zustand-card";
import { CARD_SLOT } from "@/features/cards/shared/card-routing/card-config";
import { useMediaQuery } from "@/hooks/use-media-query";
import { getCardTypeFromEntity, INFO_CARD_TYPE } from "@/features/cards/shared/card-routing/card-types";
import type { GroupItem } from "../../../../features/map-markers/device-marker/components/group-menu/types";
import { Icon } from "../../../../features/map-markers/device-marker/components/icon/icon";
import {
    makeIconConfig,
    markerIconSizeScaling,
} from "../../../../features/map-markers/device-marker/components/icon/icon-config";
import { markerVariantStyleMapping } from "../../../../features/map-markers/device-marker/configs/styles";
import { MARKER_VARIANT, type WARNING_STATUS } from "../../../../features/map-markers/device-marker/configs/variants";
import { DeviceMarker } from "../../../../features/map-markers/device-marker/device-marker";
import { DeviceMarkerAvatar } from "../../../../features/map-markers/device-marker/device-marker-avatar";
import { useStore } from "../../../../store/store";
import { getDisplayName } from "../../../device-attributes/attributes/general";
import FocusContext from "../../focus-context";
import {
    makeBadgeParams,
    makeGroupProgressRingParams,
    makeWarnings,
    WARNING_STATUS_PRIORITY,
} from "../../map-entities/shared";
import { centerInViewableArea } from "../../panning/pan";
import { useIconSize } from "../core/sizing";
import type { ClusterMapItem, DeviceMapItem } from "../types";

const MENU_ITEM_HEIGHT = 48;
const MAX_MENU_ITEMS_BEFORE_SCROLL = 6;
const MENU_PADDING = 8;
const MENU_MAX_HEIGHT = MENU_ITEM_HEIGHT * MAX_MENU_ITEMS_BEFORE_SCROLL + MENU_PADDING * 2 + 5;
const GROUP_MENU_BOTTOM_PADDING = 15;

interface DeviceGroupMapEntityIconProps {
    clusterMapItem: ClusterMapItem;
    allDeviceMapItems: DeviceMapItem[];
    mapComponent: L.Map;
    dimmed?: boolean;
    onHover: (isHovered: boolean) => void;
    hidden?: boolean;
}

/**
 * Renders a device group icon. Wraps the DeviceIcon component, contains logic for what an icon should look like
 * based on the device document and which props to pass in to the DeviceIcon component
 */
export const DeviceGroupMapEntityIcon = memo(
    ({ clusterMapItem, allDeviceMapItems, mapComponent, dimmed, onHover, hidden }: DeviceGroupMapEntityIconProps) => {
        const [dummyUpdateGroupItems, setDummyUpdateGroupItems] = useState(0);
        const closeCard = useStore((state) => state.closeCard);
        const setSelectedMapEntity = useStore((state) => state.setSelectedMapEntity);
        const isCardOpen = useStore((state) => state.slots[CARD_SLOT.BOTTOM_RIGHT].isOpen);
        const [groupWarningStatus, setGroupWarningStatus] = useState<WARNING_STATUS | undefined>(undefined);
        const iconSize = useIconSize({ mapComponent });
        const menuMaxHeight = Math.min(MENU_MAX_HEIGHT, window.innerHeight * 0.4);
        const isSmallScreen = useMediaQuery(BREAKPOINT.DEVICE_CARD_FULL_WIDTH);
        const clusterMapItemIdRef = useRef("");

        useEffect(() => {
            // clusterMapItemId is a string that is a combination of the lat, long, and device ids in the cluster, so we can use it to detect if the cluster has changed
            if (clusterMapItemIdRef.current !== clusterMapItem.id) {
                clusterMapItemIdRef.current = clusterMapItem.id;

                const uid = generateUID(`cluster_${clusterMapItem.id}_model_change_listeners`);

                const onDeviceChange = () => {
                    setDummyUpdateGroupItems((prev) => prev + 1);
                };

                // Add event listeners to members of the cluster
                allDeviceMapItems
                    .filter((item) => item.clusterKey === clusterMapItem.clusterKey)
                    .forEach((item) => {
                        item.mapEntity.model.onChange.addListener(onDeviceChange, uid);
                    });

                return () => {
                    EventHandler.disposeOfAllHooksForUID(uid);
                };
            }

            return () => {}; // for eslint consistent return type
        }, [allDeviceMapItems, clusterMapItem]);

        // Create the groupItem list from the members of the cluster
        const groupItems = useMemo(() => {
            let highestPriorityWarningStatus: WARNING_STATUS | undefined;
            const groupItemsArray: GroupItem[] = [];
            turf.featureEach(clusterMapItem.cluster, (feature) => {
                const deviceMapItem = allDeviceMapItems.find((item) => item.mapEntity.id === feature.properties?.id);
                if (!deviceMapItem || deviceMapItem.mapEntity.focusState === "hidden") {
                    return;
                }

                const { status } = makeWarnings({
                    model: deviceMapItem.mapEntity.model,
                    type: deviceMapItem.mapEntity.model.type,
                });

                if (
                    (status && !highestPriorityWarningStatus) ||
                    (status &&
                        highestPriorityWarningStatus &&
                        WARNING_STATUS_PRIORITY[status] > WARNING_STATUS_PRIORITY[highestPriorityWarningStatus])
                ) {
                    highestPriorityWarningStatus = status;
                }

                const zones = deviceMapItem.mapEntity.model?.connectedZones?.map((zone: Zone) => ({
                    id: zone.id,
                    name: zone.name,
                }));

                groupItemsArray.push({
                    zones,
                    id: deviceMapItem.mapEntity.id,
                    label: getDisplayName({ model: deviceMapItem.mapEntity.model }),
                    icon: <DeviceMarkerAvatar mapEntity={deviceMapItem.mapEntity} />,
                    onClick: (_e: React.MouseEvent<HTMLElement>) => {
                        /**
                         * With some persistent cards (such as scheduler), we dont want a click on a group menu item to open a new card
                         * and dismiss the existing card
                         */
                        if (!shouldBlockMapEntityCardOpen(FocusContext.currentInfoCard?.currentInfoCardType)) {
                            openZustandCard({
                                entity: deviceMapItem.mapEntity,
                                cardType: getCardTypeFromEntity(deviceMapItem.mapEntity),
                            });
                        } else {
                            FocusContext.onInteraction(_e, deviceMapItem.mapEntity);
                        }
                    },
                    model: deviceMapItem.mapEntity.model,
                });
            });

            // Set the badge variant to the highest priority warning status
            setGroupWarningStatus(highestPriorityWarningStatus);
            return groupItemsArray;

            // As a hack, dummyUpdateGroupItems needs to be a dependency to force a re-render of the group items based on the event listener
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [clusterMapItem, allDeviceMapItems, openZustandCard, dummyUpdateGroupItems]);

        if (hidden) {
            return <></>;
        }

        const { variant, iconConfig } = makeIconConfig({
            deviceType: "GROUP",
        });

        return (
            <DeviceMarker
                tooltip={{
                    enabled: false,
                }}
                dimmed={dimmed}
                size={iconSize}
                forceExpand={true}
                colorParams={markerVariantStyleMapping[MARKER_VARIANT.GROUP]}
                onMouseEnter={() => onHover(true)}
                onMouseLeave={() => onHover(false)}
                badgeParams={makeBadgeParams({
                    warningStatus: groupWarningStatus,
                })}
                progressRingParams={makeGroupProgressRingParams({
                    models: groupItems.map((item) => item.model),
                })}
                onClick={async () => {
                    /**
                     * On mobile, dismiss any open info cards when opening the group menu (mobile menu conflicts with info card)
                     * On desktop, only dismiss device info cards when opening the group menu
                     */
                    if (
                        FocusContext.currentInfoCard &&
                        (isMobile || FocusContext.currentInfoCard.currentInfoCardType === INFO_CARD_TYPE.DEVICE)
                    ) {
                        if (isCardOpen) {
                            setSelectedMapEntity(null);
                            closeCard(CARD_SLOT.BOTTOM_RIGHT);
                        }
                        await FocusContext.dismissCurrentInfoCard();
                    }

                    // Pan to avoid menu on mobile dimensions only
                    if (groupItems.length > 0 && isSmallScreen) {
                        const menuHeight =
                            groupItems.length <= MAX_MENU_ITEMS_BEFORE_SCROLL
                                ? MENU_ITEM_HEIGHT * groupItems.length + MENU_PADDING * 2
                                : menuMaxHeight;

                        centerInViewableArea({
                            mapComponent,
                            cardHeight: menuHeight,
                            iconLatLng: new L.LatLng(clusterMapItem.lat, clusterMapItem.long),
                            padding: GROUP_MENU_BOTTOM_PADDING,
                        });
                    }
                }}
                groupMenu={{
                    menuItems: groupItems,
                    groupMenuOptions: {
                        menuMaxHeight,
                        menuPadding: MENU_PADDING,
                        menuItemHeight: MENU_ITEM_HEIGHT,
                    },
                }}
            >
                <Icon
                    {...iconConfig}
                    size={iconSize * markerIconSizeScaling[variant]}
                    centerContent={groupItems.length.toString()}
                />
            </DeviceMarker>
        );
    },
    (prevProps, nextProps) =>
        prevProps.dimmed === nextProps.dimmed &&
        prevProps.clusterMapItem === nextProps.clusterMapItem &&
        prevProps.hidden === nextProps.hidden,
);
